import { Controller } from "@hotwired/stimulus"
import { parse } from "@elastic/esql"

export default class extends Controller {
  static targets = ["input", "validBadge", "invalidBadge", "invalidMessage", "failedBadge", "submitButton"]

  #debounce = null
  #state = "idle"
  #boundSubmitGuard = null

  connect() {
    // Mounted on the <form> element so all targets (textarea, pills, submit
    // button) are descendants. Attach the submit listener directly with
    // stopImmediatePropagation in #submitGuard so the existing
    // accounts--threatops--logs--search#submit action doesn't fire on invalid.
    this.#boundSubmitGuard = this.#submitGuard.bind(this)
    // Capture phase ensures the guard runs BEFORE the search controller's
    // bubble-phase submit action regardless of Stimulus registration order,
    // so stopImmediatePropagation actually blocks programmatic submits
    // (e.g. CodeMirror's Ctrl+Enter -> form.requestSubmit()).
    this.element.addEventListener("submit", this.#boundSubmitGuard, { capture: true })
    // Initial mount: classify state and disable submit if needed, but DON'T
    // touch failedBadge — the server may have rendered it visible (backend
    // error from the request that loaded this page); we only clear it once
    // the user starts editing.
    if (this.hasInputTarget) this.#run({ initial: true })
  }

  disconnect() {
    clearTimeout(this.#debounce)
    this.element.removeEventListener("submit", this.#boundSubmitGuard, { capture: true })
  }

  validate() {
    clearTimeout(this.#debounce)
    this.#debounce = setTimeout(() => this.#run(), 400)
  }

  guardCtrlEnter(event) {
    if (this.#state !== "invalid") return
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  #submitGuard(event) {
    if (this.#state !== "invalid") return
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  #run({ initial = false } = {}) {
    const query = (this.inputTarget.value || "").trim()
    if (!query || !query.toLowerCase().startsWith("from")) {
      this.#applyState("idle", "", { initial })
      return
    }

    // Apostrophes and unrecognised pipeline commands aren't surfaced to the
    // user — the parser may still accept them, and if the backend rejects
    // them the "Query failed" pill renders the humanized message.
    const { errors, ast } = parse(query)
    if (errors.length > 0) {
      this.#applyState("invalid", errors[0].message.replace(/^SyntaxError:\s*/, ""), { initial })
      return
    }

    const source = ast?.[0]?.args?.[0]?.name
    if (source && source !== "logs") {
      this.#applyState("invalid", "'logs' is the only supported source", { initial })
      return
    }

    this.#applyState("valid", "", { initial })
  }

  #applyState(state, msg = "", { initial = false } = {}) {
    this.#state = state
    const showValid = state === "valid"
    const showInvalid = state === "invalid"

    if (this.hasValidBadgeTarget) this.validBadgeTarget.classList.toggle("hidden", !showValid)
    if (this.hasInvalidBadgeTarget) this.invalidBadgeTarget.classList.toggle("hidden", !showInvalid)
    if (this.hasInvalidMessageTarget) this.invalidMessageTarget.textContent = showInvalid ? msg : ""
    // The server may have rendered the "Query failed" pill from a backend
    // error on the current page. Only clear it once the user starts editing.
    if (this.hasFailedBadgeTarget && !initial) this.failedBadgeTarget.classList.add("hidden")
    this.submitButtonTargets.forEach(btn => { btn.disabled = showInvalid })
  }
}
